import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard, Calendar, CalendarDays, ShoppingCart, FileText, BookOpen,
  Menu, X, ExternalLink, ChevronRight, Mail, Building2, MessageSquareQuote,
  LogOut, Users, Layers, Tag, Images, Handshake, Sparkles, Crown, Inbox, ClipboardList, Wand2 } from "lucide-react";

const NAV_ITEMS = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { path: "/admin/events", label: "Events", icon: Calendar },
  { path: "/admin/workshop-series", label: "Workshop Series", icon: Layers },
  { path: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { path: "/admin/leads", label: "Leads", icon: Inbox },
  { path: "/admin/adoption", label: "Adoption Assessment", icon: ClipboardList },
  { path: "/admin/coupons", label: "Coupons", icon: Tag },
  { path: "/admin/pro", label: "Membership", icon: Crown },
  { path: "/admin/emails", label: "Emails", icon: Mail },
  { path: "/admin/assets", label: "Brand Imagery", icon: Images },
  { path: "/admin/studio", label: "Studio", icon: Wand2 },
  { path: "/admin/case-studies", label: "Case Studies", icon: FileText },
  { path: "/admin/clients", label: "Clients", icon: Building2 },
  { path: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { path: "/admin/relationships", label: "Key Relationships", icon: Handshake },
  { path: "/admin/showcase", label: "Showcase", icon: Sparkles },
  { path: "/admin/blog", label: "Blog Posts", icon: BookOpen },
  { path: "/admin/team", label: "Team", icon: Users },
];

interface SessionUser {
  email: string;
  name: string | null;
  picture: string | null;
}

function AdminAuth({ initialError }: { initialError: string | null }) {
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#0D0A14", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}>
      <div style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: 32, backgroundColor: "rgba(255,255,255,0.02)" }}>
          <h1 style={{ fontSize: 28, color: "#fff", marginBottom: 4, fontWeight: 400, letterSpacing: "-0.5px" }} data-testid="text-admin-login-title">Admin Dashboard</h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 32 }}>AdVenture Media</p>
          <a
            href="/api/auth/google"
            style={{ width: "100%", height: 44, backgroundColor: "#fff", color: "#1F1F1F", borderRadius: 4, fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, textDecoration: "none", boxSizing: "border-box" }}
            data-testid="button-admin-login-google"
          >
            <GoogleIcon /> Sign in with Google
          </a>
          {initialError === "not_authorized" && (
            <p style={{ color: "#E0433E", fontSize: 13, marginTop: 16, textAlign: "center" }}>
              That Google account isn't on the admin whitelist. Sign in with an authorized account.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  // Inline Google "G" mark so we don't depend on an external asset/CDN.
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<SessionUser | null | "loading">("loading");

  // Surface auth errors from the OAuth callback redirect (e.g. non-whitelisted
  // accounts get bounced to /admin/login?error=not_authorized).
  const [authError, setAuthError] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("error");
  });

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (r) => {
        if (r.ok) return (await r.json()) as SessionUser;
        return null;
      })
      .then((u) => setUser(u))
      .catch(() => setUser(null));
  }, []);

  if (user === "loading") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#0D0A14", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.4)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        Checking session…
      </div>
    );
  }

  if (!user) {
    return <AdminAuth initialError={authError} />;
  }

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location === path;
    return location.startsWith(path);
  };

  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = "/admin";
  }

  return (
    <div
      className="min-h-screen bg-[#F5F3EE] flex"
      style={{ fontFamily: "var(--font-sans)" }}
    >
      <aside className={`fixed inset-y-0 left-0 z-50 w-[240px] bg-[#0D0A14] flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-white/5">
          <p className="text-white text-[16px] tracking-tight">AdVenture</p>
          <p className="text-white/30 text-[11px] font-mono uppercase tracking-widest">Admin Panel</p>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link key={item.path} href={item.path} onClick={() => setSidebarOpen(false)}>
                <div
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-[4px] cursor-pointer transition-colors ${
                    active ? "bg-[#5832D1]/15 text-[#5832D1]" : "text-white/50 hover:text-white/80 hover:bg-white/5"
                  }`}
                  data-testid={`nav-admin-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  <span className="text-[13px] font-mono uppercase tracking-wide">{item.label}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <div className="text-white/40 text-[12px] font-mono" data-testid="text-admin-current-user">
            <span className="truncate">{user.email}</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/30 hover:text-white/60 cursor-pointer transition-colors text-[12px] font-mono uppercase tracking-wide"
            data-testid="button-admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <div className="flex items-center gap-2 text-white/30 hover:text-white/60 cursor-pointer transition-colors text-[12px] font-mono uppercase tracking-wide">
              <ExternalLink className="w-3.5 h-3.5" />
              View Site
            </div>
          </a>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 min-w-0 lg:ml-[240px]">
        <header className="h-[56px] bg-white border-b border-black/5 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#1A1428]/60 cursor-pointer" data-testid="button-admin-sidebar-toggle">
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-1 text-[12px] font-mono text-[#1A1428]/30 uppercase tracking-wide">
            {NAV_ITEMS.filter(item => isActive(item.path, item.exact)).slice(-1).map(item => (
              <span key={item.path} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                {item.label}
              </span>
            ))}
          </div>

          <div />
        </header>

        <main className="p-4 lg:p-6 max-w-[1400px]">
          {children}
        </main>
      </div>
    </div>
  );
}
